// @flow

import React, { useState, useEffect } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import classNames from "classnames";
import { push } from "connected-react-router";
import { formatDate } from "@transformers/date";
import * as clientsSelectors from "@modules/client/selectors";
import {
  Button,
  ButtonGroup,
  Loader,
  GSGrid,
  SectionComponent,
  Icons,
  View,
  Pagination,
} from "@components";
import * as serviceSelectors from "@modules/service/selectors";
import actionCreators from "@modules/service/actionCreator";
import clientsActionCreators from "@modules/client/actionCreator";
import type { ClientSettingsSettingsType } from "@modules/settings/types.js";

import { NurseDeclinedReasonsEditModal } from "@modules/service/components";
import type { ApprovableContainer } from "@modules/service/types";
import type { NurseDeclinedReason } from "@modules/service/reducer/nurse-declined-reasons/types";
import type { PaginatedList } from "@types";

type PropsFromState = $Exact<{
  isLoading: boolean,
  list: PaginatedList<ApprovableContainer<NurseDeclinedReason>>,
  totalElements?: number,
  nurseDeclinedReason: ?ApprovableContainer<NurseDeclinedReason>,
  showAddModal: boolean,
  showEditModal: boolean,
  schema: ?Object,
}>;

type PropsFromDispatch = $Exact<{
  getList: typeof actionCreators.GetNurseDeclinedReasonsList,
  setShowAddModal: typeof actionCreators.SetShowAddNurseDeclinedReasonModal,
  setShowEditModal: typeof actionCreators.SetShowEditNurseDeclinedReasonModal,
  getClientService: typeof clientsActionCreators.GetServiceAbsenceManagementSettings,
  getContainer: typeof actionCreators.GetNurseDeclinedReason,
  getSchema: typeof actionCreators.GetNurseDeclinedReasonSchema,
  addReason: typeof actionCreators.AddNurseDeclinedReason,
  updateReason: typeof actionCreators.UpdateNurseDeclinedReason,
  resetReason: typeof actionCreators.ResetNurseDeclinedReason,
  push: typeof push,
}>;

type Props = $Exact<{
  clientSettings: ClientSettingsSettingsType,
  clientId: number,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type FilterState = null | boolean;

export function NurseDeclinedReasons(props: Props) {
  const [filter, setFilter]: [FilterState, Function] = useState(true);
  const [showEditModal, setShowEditModal]: [boolean, Function] = useState(false);
  const [showAddModal, setShowAddModal]: [boolean, Function] = useState(false);
  const [hasCompleteSupport, setHasCompleteSupport] = useState(
    props.clientSettings.serviceType === "COMPLETE_SUPPORT",
  );

  const columns = [
    {
      name: props.t("ID"),
      key: "id",
    },
    {
      name: props.t("NURSE_DECLINED_REASONS_TABLE_NAME"),
      align: "left",
      renderCell: ({ row }: Object) => GSGrid.render.ellipsis(row.content.reason),
    },

    {
      name: props.t("DATE_ADDED"),
      renderCell: ({ row }: Object) => GSGrid.render.date(row.content.activeFrom),
    },
    {
      name: props.t("STATUS"),
      renderCell: ({ row }: Object) =>
        GSGrid.render.statusPill(props.t(row.content.isActive ? "ACTIVE" : "INACTIVE")),
    },
    {
      name: props.t("EDIT"),
      width: 40,
      onCellClick: (row: Object) => handleGetDetails(row),
      renderCell: ({ row }: Object) => GSGrid.render.materialIcon("edit"),
    },
  ];

  useEffect(() => {
    if (!hasCompleteSupport) return;
    const { clientId } = props;
    props.getList({
      clientId,
      isActive: filter,
    });
    props.getClientService({
      clientId,
    });
    props.getSchema();
  }, [props.clientId, filter]);

  function handleGetDetails(record: Object) {
    props.getContainer({
      clientId: record.clientId,
      reasonId: record.id,
    });
    setShowEditModal(true);
  }

  function handleAddSetup() {
    setShowAddModal(true);
  }

  function handleChangeFilter(filter: FilterState) {
    return function() {
      setFilter(filter);
    };
  }

  function handleModalAction(d: Object) {
    const action = showEditModal ? props.updateReason : props.addReason;
    if (showEditModal) {
      props.updateReason({
        clientId: props.clientId,
        reasonId: props.nurseDeclinedReason?.id,
        draft: d.draft,
        isActive: filter,
      });
    } else {
      props.addReason({
        clientId: props.clientId,
        draft: d.draft,
        isActive: filter,
      });
    }
    handleModalClose();
  }

  function handleModalClose() {
    if (showEditModal == true) {
      props.resetReason();
    }
    setShowEditModal(false);
    setShowAddModal(false);
  }

  function handleChangePage(page: number) {
    props.getList({
      page,
      clientId: props.clientId,
      isActive: filter,
    });
  }

  function pagination() {
    const { list } = props;
    return list
      ? {
          totalPages: Math.ceil(list.totalElements / list.pagination.pageSize),
          pageNumber: list.pagination.pageNumber,
          isActive: filter,
        }
      : null;
  }

  return (
    (hasCompleteSupport && (
      <View>
        {props.isLoading && <Loader inline />}
        <View justifyContent="space-between" direction="row">
          <View direction="row">
            <ButtonGroup>
              <Button
                color={filter === null ? "selected" : null}
                onClick={handleChangeFilter(null)}
              >
                {props.t("ALL")}
              </Button>
              <Button
                color={filter === true ? "selected" : null}
                onClick={handleChangeFilter(true)}
              >
                {props.t("ACTIVE")}
              </Button>
              <Button
                color={filter === false ? "selected" : null}
                onClick={handleChangeFilter(false)}
              >
                {props.t("INACTIVE")}
              </Button>
            </ButtonGroup>
          </View>

          <Button materialIcon="add_circle" onClick={handleAddSetup}>
            {props.t("ADD")}
          </Button>
        </View>
        {props.list?.content?.length ? (
          <GSGrid className="mt-3" columns={columns} rows={props.list?.content} />
        ) : (
          <div className="my-5 d-flex justify-content-center">
            <h3 className="text-pinkish-grey">{props.t("NURSE_DECLINED_REASONS_LIVE_REASONS")}</h3>
          </div>
        )}
        {pagination && (
          <Pagination
            className="my-3 d-flex flex-fill justify-content-center"
            currentPage={pagination.pageNumber}
            totalPages={pagination.totalPages}
            onChange={handleChangePage}
          />
        )}
        {(showAddModal || showEditModal) && props.schema && (
          <NurseDeclinedReasonsEditModal
            editing={showEditModal}
            reason={props.nurseDeclinedReason}
            schema={props.schema}
            onCancel={handleModalClose}
            onSubmit={handleModalAction}
          />
        )}
      </View>
    )) || (
      <div className="card bg-light">
        <div className="card-body">
          <h6 className="m-0">{props.t("ESSENTIALS_SERVICE_TYPE_MESSAGE")}</h6>
        </div>
      </div>
    )
  );
}

const mapStateToProps = (state): PropsFromState => {
  const list = serviceSelectors.getNurseDeclinedReasonsList(state);
  return {
    isLoading: serviceSelectors.isLoading(state),
    list: list,
    totalElements: list?.totalElements,
    schema: serviceSelectors.getNurseDeclinedReasonSchema(state),
    showAddModal: serviceSelectors.getShowNurseDeclinedReasonAddModal(state),
    showEditModal: serviceSelectors.getShowNurseDeclinedReasonEditModal(state),
    nurseDeclinedReason: serviceSelectors.getCurrentNurseDeclinedReason(state),
    isLoading: serviceSelectors.getNurseDeclinedReasonsLoading(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getList: actionCreators.GetNurseDeclinedReasonsList,
  setShowAddModal: actionCreators.SetShowAddNurseDeclinedReasonModal,
  setShowEditModal: actionCreators.SetShowEditNurseDeclinedReasonModal,
  getClientService: clientsActionCreators.GetServiceAbsenceManagementSettings,
  getContainer: actionCreators.GetNurseDeclinedReason,
  getSchema: actionCreators.GetNurseDeclinedReasonSchema,
  addReason: actionCreators.AddNurseDeclinedReason,
  updateReason: actionCreators.UpdateNurseDeclinedReason,
  resetReason: actionCreators.ResetNurseDeclinedReason,
  push,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(NurseDeclinedReasons);
